import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { logSystemEvent } from '@/lib/errors';

interface Props { children: ReactNode; routeLabel?: string; }
interface State { error: Error | null; info: ErrorInfo | null; }

// Wrap each top-level route so a render error in one screen never
// takes down the whole shell. Pairs with the global ErrorBoundary in
// App.tsx — that one catches provider/auth failures, this one catches
// per-page failures so users can navigate away.
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[RouteErrorBoundary]', this.props.routeLabel ?? 'route', error, info);
    this.setState({ info });
    void logSystemEvent({
      severity: 'critical',
      module: 'route',
      message: `[route:${this.props.routeLabel ?? 'unknown'}] ${error.message}`,
      stack: error.stack ?? null,
      context: { componentStack: info.componentStack?.slice(0, 4000) ?? null },
    });
  }

  reset = () => this.setState({ error: null, info: null });

  copyDiagnostics = async () => {
    const { error, info } = this.state;
    const payload = [
      `Route: ${this.props.routeLabel ?? 'unknown'}`,
      `Time: ${new Date().toISOString()}`,
      `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
      `Message: ${error?.message ?? 'n/a'}`,
      '',
      'Stack:',
      error?.stack ?? 'n/a',
      '',
      'Component stack:',
      info?.componentStack ?? 'n/a',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(payload);
      toast({ title: 'Diagnostics copied', description: 'Paste this into a support thread to help us reproduce.' });
    } catch {
      toast({ title: 'Could not copy diagnostics', variant: 'destructive' });
    }
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
          <AlertCircle className="h-6 w-6" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">Something went wrong on this page</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {error.message || 'An unexpected error occurred.'} You can reload this page or copy the
          diagnostics so we can investigate.
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <Button onClick={this.reset} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Try again
          </Button>
          <Button variant="outline" onClick={this.copyDiagnostics} className="gap-1.5">
            <Copy className="h-3.5 w-3.5" /> Copy diagnostics
          </Button>
        </div>
      </div>
    );
  }
}
